// data-fit LLC · DecisionFlow · Amendment Governance
// AmendmentBeginModal.jsx · API: v1.27 · S247 Phase 10 · S619 CCM · S622
// OI-AMENDMENT-UI-BEGIN-MODAL
//
// Renders before any governed entity EDIT (not create). Captures reason_code + note,
// calls POST /api/v1/amendments/begin (single) or POST /api/v1/amendments/begin-package
// (when packageMode = true), returns amendment_id/package_id to caller via onBegin.
//
// Props:
//   open            boolean   — controls visibility
//   entityType      string    — amended_entity_type (e.g. "exchange_rate")
//   entityId        string    — UUID of the existing entity being amended (required by API)
//   fiscalPeriodId  number    — required for begin payload
//   wizardSessionId string    — UUID of active wizard session (derived server-side if null)
//   onBegin         fn(amendmentId) — called with amendment_id (or package_id) on success
//   onCancel        fn()      — called when user dismisses without beginning
//   packageMode     boolean   — when true: title = "Begin Change Package",
//                               calls POST /api/v1/amendments/begin-package (S619 CCM §5.2)
//   periodLabel     string    — S622: display-only period label (e.g. "FY2026 M04")
//   periodStatus    string    — S622: display-only period status (e.g. "open")
//
// Design note (DR-007):
//   amended_entity_id is REQUIRED by the backend — it fetches before_state from the
//   existing entity row. This modal therefore only applies to EDITING existing entities.
//   Creating a new entity does NOT require an amendment (no before_state exists).
//
// Reason codes must match amendment_log_reason_check constraint exactly.
// Visibility: comp_admin only — caller is responsible for not mounting for other roles.
// NN-03: no AI in this path. No calculation calls here.

import React, { useState } from 'react'

const API_BASE = '/api/v1'

// Must match amendment_log_reason_check DB constraint exactly.
const REASON_CODES = [
  { value: 'data_correction',   label: 'Data correction' },
  { value: 'fx_rate_correction', label: 'FX rate correction' },
  { value: 'rate_table_fix',    label: 'Rate table fix' },
  { value: 'plan_design_fix',   label: 'Plan design fix' },
  { value: 'finance_adjustment', label: 'Finance adjustment' },
  { value: 'quota_correction',  label: 'Quota correction' },
  { value: 'deal_correction',   label: 'Deal correction' },
  { value: 'territory_change',  label: 'Territory change' },
  { value: 'credit_correction', label: 'Credit correction' },
  { value: 'role_change',       label: 'Role change' },        // S619 — CCM
  { value: 'plan_transition',   label: 'Plan transition' },    // S619 — CCM
  { value: 'segment_change',    label: 'Segment change' },     // S619 — CCM
  { value: 'other',             label: 'Other' },
]

function getErrorMessage(payload) {
  // Backend shape: { detail: { error: { code, message, ... } } }
  // payload?.error was wrong — the envelope is payload?.detail?.error.
  const err = payload?.detail?.error ?? payload?.error
  if (!err) return 'Unable to begin amendment.'
  if (err.code === 'PERIOD_NOT_MUTABLE')         return err.message || 'The period is not mutable. Check period status and session state.'
  if (err.code === 'AMENDMENT_CONFLICT')         return err.message || 'An open amendment already exists for this entity.'
  if (err.code === 'AMENDMENT_TENANT_MISMATCH')  return 'Tenant mismatch — cannot begin amendment.'
  if (err.code === 'AMENDMENT_SESSION_MISMATCH') return 'Session mismatch — cannot begin amendment.'
  if (err.code === 'ENTITY_NOT_FOUND')           return 'Entity not found — it may have been deleted.'
  if (err.code === 'VALIDATION_ERROR')           return err.message || 'Request validation failed.'
  return err.message || err.recovery_action || 'Unable to begin amendment.'
}

// S622 — OI-CCM-PERIOD-CONTEXT
// Period status badge shown in the modal so users confirm the target period before
// submitting. periodLabel/periodStatus are display-only — fiscalPeriodId is still
// the source of truth for the API call. A non-open status renders an amber warning
// because begin_amendment gates 1–4 will reject it with PERIOD_NOT_MUTABLE.
function PeriodContextBadge({ periodLabel, periodStatus, fiscalPeriodId }) {
  if (!periodLabel && !fiscalPeriodId) return null

  const label  = periodLabel ?? `Period ${fiscalPeriodId}`
  const status = periodStatus ?? null
  const isOpen = status === 'open'

  return (
    <div
      className={[
        'flex items-center justify-between rounded-xl border px-3 py-2.5',
        isOpen
          ? 'border-[rgb(var(--color-success))]/30 bg-[rgb(var(--color-success))]/5'
          : status
          ? 'border-[rgb(var(--color-warning))]/40 bg-[rgb(var(--color-warning))]/8'
          : 'border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))]',
      ].join(' ')}
    >
      <div className="flex items-center gap-2">
        <span className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
          Period
        </span>
        <span className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">
          {label}
        </span>
      </div>
      <div className="flex items-center gap-2">
        {status && (
          <span
            className={[
              'rounded-full px-2 py-0.5 text-tok-xs font-semibold',
              isOpen
                ? 'bg-[rgb(var(--color-success))]/15 text-[rgb(var(--color-success))]'
                : 'bg-[rgb(var(--color-warning))]/20 text-[rgb(var(--color-warning))]',
            ].join(' ')}
          >
            {status}
          </span>
        )}
        {!isOpen && status && (
          <span className="text-tok-xs text-[rgb(var(--color-warning))]">
            ⚠ Amendment requires an open period
          </span>
        )}
      </div>
    </div>
  )
}

export default function AmendmentBeginModal({
  open,
  entityType,
  entityId,
  fiscalPeriodId,
  wizardSessionId,
  onBegin,
  onCancel,
  packageMode  = false,  // S619 — CCM §5.2: when true, calls begin-package endpoint
  // S622 — OI-CCM-PERIOD-CONTEXT: display-only period metadata so the user can
  // confirm which period the amendment will be opened against before submitting.
  // Not used in the API call — fiscalPeriodId remains the source of truth.
  periodLabel  = null,   // e.g. "FY2026 M04"
  periodStatus = null,   // e.g. "open" | "closed" | "locked"
}) {
  const [reasonCode, setReasonCode] = useState(REASON_CODES[0].value)
  const [note, setNote]             = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError]           = useState(null)

  if (!open) return null

  const handleBegin = async () => {
    setError(null)
    if (!fiscalPeriodId) { setError('No active fiscal period — cannot begin amendment.'); return }
    if (!entityType)     { setError('Entity type not specified — cannot begin amendment.'); return }
    if (!entityId)       { setError('Entity ID not provided — amendment requires an existing entity.'); return }

    setSubmitting(true)
    try {
      const body = {
        fiscal_period_id:      Number(fiscalPeriodId),
        amended_entity_type:   entityType,
        amended_entity_id:     String(entityId),
        amendment_reason_code: reasonCode,
      }
      if (note.trim()) body.amendment_reason_note = note.trim()
      // wizard_session_id: backend derives from JWT + fiscal_period_id if not sent,
      // but we send it when available to be explicit.
      if (wizardSessionId) body.wizard_session_id = String(wizardSessionId)

      // S619 — CCM §5.2: packageMode routes to begin-package; single otherwise
      const endpoint = packageMode
        ? `${API_BASE}/amendments/begin-package`
        : `${API_BASE}/amendments/begin`

      const res = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const payload = await res.json().catch(() => ({}))
      if (!res.ok) { setError(getErrorMessage(payload)); return }

      // packageMode returns change_package_id; single mode returns amendment_id
      const amendmentId = packageMode
        ? (payload?.change_package_id ?? payload?.data?.change_package_id)
        : (payload?.amendment_id ?? payload?.amendment?.amendment_id ?? payload?.data?.amendment_id)
      if (!amendmentId) { setError('Amendment created but ID not returned — contact support.'); return }

      // Reset for next use
      setReasonCode(REASON_CODES[0].value)
      setNote('')
      setError(null)
      onBegin(amendmentId)
    } catch (e) {
      setError(e.message || 'Network error.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleCancel = () => {
    if (submitting) return
    setReasonCode(REASON_CODES[0].value)
    setNote('')
    setError(null)
    onCancel()
  }

  const entityLabel = entityType
    ? entityType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    : 'Entity'

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => { if (e.target === e.currentTarget) handleCancel() }}
      aria-modal="true"
      role="dialog"
      aria-labelledby="amd-modal-title"
    >
      <div className="relative w-full max-w-md rounded-2xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] p-6 shadow-xl flex flex-col gap-5">

        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-tok-xs font-semibold uppercase tracking-widest text-[rgb(var(--color-warning))] mb-1">
              Amendment Required
            </p>
            <h2 id="amd-modal-title" className="text-tok-lg font-bold text-[rgb(var(--color-text))]">
              {packageMode ? 'Begin Change Package' : 'Begin Amendment'}
            </h2>
            <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
              You are editing a governed entity: <strong className="text-[rgb(var(--color-text))]">{entityLabel}</strong>.
              An amendment must be opened before any changes can be saved.
            </p>
          </div>
          <button
            type="button"
            onClick={handleCancel}
            disabled={submitting}
            aria-label="Cancel amendment"
            className="rounded-lg p-1.5 text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-40"
          >
            ✕
          </button>
        </div>

        {/* S622 — Period context: user confirms which period before submitting */}
        <PeriodContextBadge
          periodLabel={periodLabel}
          periodStatus={periodStatus}
          fiscalPeriodId={fiscalPeriodId}
        />

        {/* Error */}
        {error && (
          <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
            {error}
          </div>
        )}

        {/* Reason code */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="amd-reason-code" className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
            Reason <span className="text-[rgb(var(--color-danger))]">*</span>
          </label>
          <select
            id="amd-reason-code"
            value={reasonCode}
            onChange={(e) => setReasonCode(e.target.value)}
            disabled={submitting}
            className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] focus:outline-none disabled:opacity-50"
          >
            {REASON_CODES.map((rc) => (
              <option key={rc.value} value={rc.value}>{rc.label}</option>
            ))}
          </select>
        </div>

        {/* Note */}
        <div className="flex flex-col gap-1.5">
          <label htmlFor="amd-note" className="text-tok-xs font-medium text-[rgb(var(--color-text-muted))]">
            Note <span className="text-tok-xs font-normal">(optional)</span>
          </label>
          <textarea
            id="amd-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            disabled={submitting}
            rows={3}
            maxLength={500}
            placeholder="Describe the change you are making…"
            className="w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] placeholder:text-[rgb(var(--color-text-muted))] focus:outline-none disabled:opacity-50 resize-none"
          />
          <p className="text-tok-xs text-[rgb(var(--color-text-muted))] text-right">{note.length}/500</p>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-1">
          <button
            type="button"
            onClick={handleBegin}
            disabled={submitting}
            className="rounded-xl bg-[rgb(var(--color-warning))] px-4 py-2 text-tok-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
          >
            {submitting
              ? (packageMode ? 'Opening package…' : 'Opening amendment…')
              : (packageMode ? 'Begin Change Package' : 'Begin Amendment')}
          </button>
          <button
            type="button"
            onClick={handleCancel}
            disabled={submitting}
            className="rounded-xl border border-[rgb(var(--color-border))] px-4 py-2 text-tok-sm font-medium text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50"
          >
            Cancel
          </button>
        </div>

        {/* Governance notice */}
        <p className="text-tok-xs text-[rgb(var(--color-text-muted))] border-t border-[rgb(var(--color-border))] pt-3">
          This amendment will be tracked in the audit log. Affected wizard stages will require reapproval after your change is committed.
          Amendments expire after 24 hours if not committed.
        </p>
      </div>
    </div>
  )
}
